import { type Participant, renderGender, renderParticipantName } from '@piccolohealth/pbs-common';
import { ChakraV3, FancyDate } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import AvatarMaleImage from '../../assets/avatar-male.png';
import { FormSection } from '../../components/forms/FormSection';
import { FormStack } from '../../components/forms/FormStack';
import { ProfileItem } from '../../components/generic/ProfileItem';
import { ParticipantStatusTag } from '../participant/ParticipantStatusTag';

interface Props {
  participant: Participant;
  dob: string;
  age: number;
}

export const ParticipantProfileCardGeneral = (props: Props) => {
  const { participant, dob, age } = props;

  return (
    <FormStack>
      <FormSection heading='Basic details'>
        <ChakraV3.Grid w='full' templateColumns='repeat(2, 1fr)' gap={4} rowGap={6}>
          <ProfileItem label='Name'>{renderParticipantName(participant)}</ProfileItem>
          <ProfileItem label='Gender'>{renderGender(participant.gender)}</ProfileItem>
          <ProfileItem label='Created'>
            <FancyDate date={participant.createdAt} showHumanized={false} />
          </ProfileItem>
          <ProfileItem label='Date of birth'>
            {dob} - {age} years old
          </ProfileItem>
          <ProfileItem label='Status'>
            <ParticipantStatusTag status={participant.status} size='sm' />
          </ProfileItem>
          <ProfileItem label='Clinic location'>
            <ChakraV3.Tag w='fit-content' size='sm' colorPalette={participant.location.color}>
              {participant.location.name}
            </ChakraV3.Tag>
          </ProfileItem>
          <ProfileItem label='NDIS number'>{participant.ndisNumber ?? '-'}</ProfileItem>
          <ProfileItem label='Email'>{participant.email ?? '-'}</ProfileItem>
          <ProfileItem label='Phone number'>{participant.phone ?? '-'}</ProfileItem>

          <ProfileItem label='Labels'>
            {P.isEmpty(participant.labels) ? (
              '-'
            ) : (
              <ChakraV3.Wrap pt={2}>
                {participant.labels.map((label) => {
                  return (
                    <ChakraV3.WrapItem key={label.id}>
                      <ChakraV3.Tag w='fit-content' size='sm' colorPalette={label.color}>
                        {label.name}
                      </ChakraV3.Tag>
                    </ChakraV3.WrapItem>
                  );
                })}
              </ChakraV3.Wrap>
            )}
          </ProfileItem>
          <ProfileItem label='Profile picture'>
            <ChakraV3.Avatar mt={1} src={participant.picture ?? AvatarMaleImage} size='lg' />
          </ProfileItem>
        </ChakraV3.Grid>
      </FormSection>

      <ChakraV3.Separator />

      <FormSection heading='Address details'>
        <ChakraV3.Grid w='full' templateColumns='repeat(2, 1fr)' gap={4} rowGap={6}>
          <ProfileItem label='Address'>{participant.address ?? '-'}</ProfileItem>
          <ProfileItem label='Suburb'>{participant.suburb ?? '-'}</ProfileItem>
          <ProfileItem label='State'>{participant.state ?? '-'}</ProfileItem>
          <ProfileItem label='Postcode'>{participant.postcode ?? '-'}</ProfileItem>
        </ChakraV3.Grid>
      </FormSection>
    </FormStack>
  );
};
